import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { schoolApi } from '../api/school.api';
import { useAuthStore } from '../store/auth.store';

type Status = 'idle' | 'checking' | 'valid' | 'reconnect_required' | 'error';

export function useReservationAccess() {
  const [status, setStatus] = useState<Status>('idle');
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const check = useCallback(async () => {
    if (pending.current) return false;
    pending.current = true;
    setStatus('checking');
    const school = useAuthStore.getState().activeSchoolId;
    const user = useAuthStore.getState().user?.id;
    const current = () => mounted.current && school === useAuthStore.getState().activeSchoolId && user === useAuthStore.getState().user?.id;
    try {
      const result = await schoolApi.checkReservationAccess();
      if (!current()) return false;
      const next = result.status === 'valid' ? 'valid' : result.status === 'reconnect_required' ? 'reconnect_required' : 'error';
      setStatus(next);
      return next === 'valid';
    } catch {
      if (current()) setStatus('error');
      return false;
    } finally { pending.current = false; }
  }, []);
  return { status, check };
}

export function ReservationAccessMessage({ status, retry, close }: { status: Status; retry: () => void; close?: () => void }) {
  const role = useAuthStore(s => s.user?.role?.toLowerCase());
  const canConnect = role === 'owner' || role === 'manager';
  const [connecting, setConnecting] = useState(false);
  const [failed, setFailed] = useState(false);
  const { pathname } = useLocation();
  async function connect() {
    setConnecting(true); setFailed(false);
    const school = useAuthStore.getState().activeSchoolId;
    try {
      const target = /^\/(dashboard|requests|etps|windows)(\/|$)/.test(pathname) ? pathname : '/requests';
      const result = await schoolApi.startRdvPermisOauth({ redirectUri: window.location.origin + target });
      if (school === useAuthStore.getState().activeSchoolId) window.location.assign(result.authorizationUrl);
    } catch { setFailed(true); }
    finally { setConnecting(false); }
  }
  const busy = status === 'idle' || status === 'checking';
  return <section className="billing-notice" aria-busy={busy}>
    <h2>Connexion RDVPermis</h2>
    {busy ? <p role="status">Vérification de votre connexion RDVPermis…</p> : <>
      <p role="alert">{status === 'error' ? 'Impossible de vérifier la connexion RDVPermis. Réessayez dans quelques instants.' : 'Votre connexion RDVPermis est absente ou a expiré. Reconnectez-vous pour accéder à la réservation automatique.'}</p>
      {failed && <p role="alert">La reconnexion n’a pas pu démarrer. Réessayez.</p>}
      {status === 'reconnect_required' && !canConnect && <p>Demandez au propriétaire ou au gestionnaire de reconnecter RDVPermis.</p>}
      <div className="billing-navigation__tabs">
        {status === 'reconnect_required' && canConnect && <button className="btn btn--primary" disabled={connecting} onClick={() => void connect()}>Reconnecter RDVPermis</button>}
        <button className="btn btn--secondary" disabled={connecting} onClick={retry}>Réessayer</button>
        {close ? <button className="btn btn--ghost" onClick={close}>Annuler</button> : <Link className="btn btn--ghost" to="/lessons">Retour au planning</Link>}
      </div>
    </>}
  </section>;
}

export function ReservationAccessDialog({ status, retry, close }: { status: Status; retry: () => void; close: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => { const element = dialog.current; element?.showModal(); return () => element?.close(); }, []);
  return <dialog ref={dialog} className="reservation-access-dialog" aria-label="Connexion RDVPermis" onCancel={close}>
    <ReservationAccessMessage status={status} retry={retry} close={close} />
  </dialog>;
}

export function ReservationRouteAccess({ children }: { children: ReactNode }) {
  const school = useAuthStore(s => s.activeSchoolId);
  const user = useAuthStore(s => s.user?.id);
  const location = useLocation();
  // Nouvelle vérification à chaque page (chemin), pas à chaque paramètre d'adresse : filtres, recherche et panneau
  // ouvert (?statut=, ?q=, ?demande=…) ne remontent pas la page ni ne refont l'appel à chaque frappe.
  return <ReservationRouteCheck key={`${school}:${user}:${location.pathname}`}>{children}</ReservationRouteCheck>;
}
function ReservationRouteCheck({ children }: { children: ReactNode }) {
  const { status, check } = useReservationAccess();
  useEffect(() => { void check(); }, [check]); // Contrôle à chaque montage/navigation, jamais servi depuis un cache.
  return status === 'valid' ? <>{children}</> : <ReservationAccessMessage status={status} retry={() => void check()} />;
}
